// src/components/tickets/TicketListPage.tsx
//
// The ticket-desk list surface, mounted three times (one per view): My tickets
// (home, `/`), All open (`/tickets/open`), Closed (`/tickets/closed`). One
// component, parameterized by a per-view config (title, header-count copy, view
// key, empty-state copy). Reads flow component → useTickets → ticketsService
// (AD-3); this story has NO write path.
//
// Behavior: 25/page pagination (never infinite scroll); five combinable filters
// (Story 6.3 added the reference-code lookup) applied server-side, persisted per
// view per session (sessionStorage) with
// page reset to 1 on any change; live silent refetch via the yak_tickets
// subscription inside useTickets; skeleton rows on cold load; calm empty/error
// states.

import { useState, useEffect, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { useTickets } from '../../hooks/useTickets'
import { ticketsService } from '../../services/ticketsService'
import { loadFilters, saveFilters } from '../../utils/viewFilters'
import { TICKETS_PAGE_SIZE } from '../../utils/ticketQuery'
import { TicketRow } from './TicketRow'
import { TicketFilterBar } from './TicketFilterBar'
import type { YakTicketViewKey, YakTicketFilters } from '../../types/yak'

interface TicketViewConfig {
  view: YakTicketViewKey
  title: string
  // Header count in the calm counts-and-verbs voice; N is the filtered total.
  headerCount: (n: number) => string
}

const TICKET_VIEW_CONFIG: Record<YakTicketViewKey, TicketViewConfig> = {
  mine: {
    view: 'mine',
    title: 'My tickets',
    headerCount: n =>
      `${n} ${n === 1 ? 'ticket needs' : 'tickets need'} a response.`,
  },
  all_open: {
    view: 'all_open',
    title: 'All open',
    headerCount: n =>
      `${n} open ${n === 1 ? 'ticket' : 'tickets'} across the organization.`,
  },
  closed: {
    view: 'closed',
    title: 'Closed',
    headerCount: n => `${n} closed ${n === 1 ? 'ticket' : 'tickets'}.`,
  },
}

const COLUMNS = [
  'Question',
  'Type',
  'Brand',
  'Status',
  'Route',
  'Assignee',
  'Age',
]
const thClass =
  'px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'

function SkeletonRows() {
  return (
    <>
      {Array.from({ length: 6 }).map((_, i) => (
        <tr key={i} className="animate-pulse">
          {COLUMNS.map(col => (
            <td key={col} className="px-6 py-4">
              <div className="h-4 rounded bg-gray-200" />
            </td>
          ))}
        </tr>
      ))}
    </>
  )
}

function EmptyState({
  view,
  reference,
}: {
  view: YakTicketViewKey
  reference?: string | null
}) {
  // Story 6.3 review: a reference lookup that finds nothing is "no match", NOT
  // "nothing here". Rendering the view's own empty copy told the operator
  // "You're all caught up." for a code they had just been read over the phone —
  // a false statement about the desk, and it hid whether the code was wrong or
  // simply elsewhere. The lookup now spans every view, so a miss really does
  // mean no such code in this org.
  const code = (reference ?? '').trim()
  if (code) {
    return (
      <div className="py-12 text-center">
        <p className="text-gray-900 font-medium">
          No ticket matches reference &ldquo;{code}&rdquo;.
        </p>
        <p className="mt-1 text-sm text-gray-500">
          Check the code with the rep — searching covers open and closed tickets
          across the whole desk.
        </p>
      </div>
    )
  }
  if (view === 'mine') {
    return (
      <div className="py-12 text-center">
        <p className="text-gray-900 font-medium">You&apos;re all caught up.</p>
        <Link
          to="/tickets/open"
          className="mt-2 inline-block text-sm text-brand-600 hover:text-brand-700 hover:underline"
        >
          View all open tickets
        </Link>
      </div>
    )
  }
  const copy =
    view === 'all_open' ? 'No open tickets.' : 'No closed tickets yet.'
  return (
    <div className="py-12 text-center">
      <p className="text-gray-500">{copy}</p>
    </div>
  )
}

export function TicketListPage({ view }: { view: YakTicketViewKey }) {
  const config = TICKET_VIEW_CONFIG[view]

  const [filters, setFilters] = useState<YakTicketFilters>(() =>
    loadFilters(view)
  )
  const [page, setPage] = useState(1)
  const [brandOptions, setBrandOptions] = useState<string[]>([])

  // Brand options are the filterable universe of brands tickets actually carry
  // (Decision 7). Fetched once per view mount; a failure just leaves the brand
  // filter empty (the other filters still work) — never blocks the list.
  useEffect(() => {
    let cancelled = false
    ticketsService
      .listBrandOptions()
      .then(brands => {
        if (!cancelled) setBrandOptions(brands)
      })
      .catch(err => {
        console.error('Brand options load failed', err)
      })
    return () => {
      cancelled = true
    }
  }, [view])

  const { tickets, total, loading, error, refetch } = useTickets(
    view,
    filters,
    page
  )

  const handleFilterChange = useCallback(
    (next: YakTicketFilters) => {
      saveFilters(view, next)
      setFilters(next)
      setPage(1) // any filter change resets pagination to page 1
    },
    [view]
  )

  const totalPages = Math.max(1, Math.ceil(total / TICKETS_PAGE_SIZE))
  const firstRow = total === 0 ? 0 : (page - 1) * TICKETS_PAGE_SIZE + 1
  const lastRow = Math.min(page * TICKETS_PAGE_SIZE, total)
  const isColdLoad = loading && tickets.length === 0

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">{config.title}</h1>
        <p className="mt-1 text-sm text-gray-600">
          {config.headerCount(total)}
        </p>
      </div>

      <TicketFilterBar
        filters={filters}
        brandOptions={brandOptions}
        onChange={handleFilterChange}
      />

      {error && (
        <div className="alert alert-error flex items-center justify-between">
          <span>{error}</span>
          <button
            type="button"
            onClick={() => refetch()}
            className="text-sm font-medium underline"
          >
            Retry
          </button>
        </div>
      )}

      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {COLUMNS.map(col => (
                  <th key={col} className={thClass}>
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {isColdLoad ? (
                <SkeletonRows />
              ) : tickets.length === 0 ? (
                <tr>
                  <td colSpan={COLUMNS.length}>
                    <EmptyState view={view} reference={filters.reference} />
                  </td>
                </tr>
              ) : (
                tickets.map(ticket => (
                  <TicketRow key={ticket.id} ticket={ticket} />
                ))
              )}
            </tbody>
          </table>
        </div>

        {total > 0 && (
          <div className="flex items-center justify-between border-t border-gray-200 px-6 py-3">
            <p className="text-sm text-gray-600">
              Showing {firstRow}&ndash;{lastRow} of {total}
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 disabled:cursor-not-allowed disabled:opacity-50 hover:bg-gray-50"
              >
                Previous
              </button>
              <button
                type="button"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 disabled:cursor-not-allowed disabled:opacity-50 hover:bg-gray-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
